import { type FC, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Calendar, Users, Trophy, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button } from '@/components/ui';
import { supabase } from '@/lib/api/supabase';
import { REGISTRATIONS_CLOSED } from '@/lib/registrationsClosed';
import { ClosedNotice } from '@/components/shared';

interface AboutSection {
  id: string;
  section_key: string;
  title: string;
  content: string;
  image_url: string | null;
  display_order: number;
}

interface SiteSettings {
  [key: string]: string;
}

/**
 * About page with event information fetched from database
 */
export const AboutPage: FC = () => {
  const [sections, setSections] = useState<AboutSection[]>([]);
  const [settings, setSettings] = useState<SiteSettings>({});
  const [hotelBookingUrl, setHotelBookingUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true);

        // Fetch about sections
        const { data: sectionsData } = await supabase
          .from('about_sections')
          .select('*')
          .eq('is_published', true)
          .order('display_order', { ascending: true });

        // Fetch site settings
        const { data: settingsData } = await supabase
          .from('site_settings')
          .select('setting_key, setting_value');

        if (sectionsData) {
          setSections(sectionsData);
        }

        if (settingsData) {
          const settingsMap = settingsData.reduce((acc, item) => {
            acc[item.setting_key] = item.setting_value;
            return acc;
          }, {} as SiteSettings);
          setSettings(settingsMap);
        }

        // Fetch active hotel partner's booking URL
        const { data: hotelData } = await supabase
          .from('hotel_partners')
          .select('booking_url')
          .eq('is_active', true)
          .maybeSingle();

        if (hotelData?.booking_url) {
          setHotelBookingUrl(hotelData.booking_url);
        }
      } catch (err) {
        console.error('Failed to fetch about page data:', err);
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, []);

  const getSection = (key: string): AboutSection | undefined => {
    return sections.find((s) => s.section_key === key);
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-orange-500 border-r-transparent"></div>
          <p className="mt-4 text-white">Loading...</p>
        </div>
      </div>
    );
  }

  const hero = getSection('hero');
  const welcome = getSection('welcome');
  const scheduleFriday = getSection('schedule_friday');
  const scheduleSaturday = getSection('schedule_saturday');
  const scheduleSunday = getSection('schedule_sunday');
  const registrationInfo = getSection('registration_info');
  const venueInfo = getSection('venue_info');
  const hotelInfo = getSection('hotel_info');
  const contact = getSection('contact');

  return (
    <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
      {/* Hero Section */}
      {hero && (
        <section className="relative min-h-[300px] flex items-center justify-center py-12">
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center text-white">
              <h1 className="text-5xl md:text-6xl font-viking mb-4 drop-shadow-lg">
                {hero.title}
              </h1>
              <p className="text-xl font-medium drop-shadow-md">
                {hero.content}
              </p>
            </div>
          </div>
        </section>
      )}

      <div className="container mx-auto px-4 py-12 max-w-5xl">

        {/* Quick Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 border-turquoise-500">
            <CardHeader>
              <Calendar className="h-8 w-8 text-orange-500 mb-2" />
              <CardTitle className="text-lg">When</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{settings.event_date || 'June 14-16, 2025'}</p>
              <p className="text-xs text-muted-foreground mt-1">{settings.event_date_subtitle || 'Full weekend event'}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 border-turquoise-500">
            <CardHeader>
              <MapPin className="h-8 w-8 text-orange-500 mb-2" />
              <CardTitle className="text-lg">Where</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{settings.event_venue || 'Phoenix Convention Center'}</p>
              <p className="text-xs text-muted-foreground mt-1">{settings.event_venue_city || 'Phoenix, Arizona'}</p>
              {hotelBookingUrl && (
                <Button
                  size="sm"
                  className="w-full mt-3 bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => window.open(hotelBookingUrl, '_blank')}
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  Book Hotel
                </Button>
              )}
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 border-turquoise-500">
            <CardHeader>
              <Users className="h-8 w-8 text-orange-500 mb-2" />
              <CardTitle className="text-lg">Attendees</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{settings.event_attendees || '200+ fighters expected'}</p>
              <p className="text-xs text-muted-foreground mt-1">{settings.event_attendees_subtitle || 'From 15+ schools'}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 border-turquoise-500">
            <CardHeader>
              <Trophy className="h-8 w-8 text-orange-500 mb-2" />
              <CardTitle className="text-lg">Tournaments</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{settings.event_tournaments || '4 tournament categories'}</p>
              <p className="text-xs text-muted-foreground mt-1">{settings.event_tournaments_subtitle || 'Multiple divisions'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Primary Content */}
          <div className="lg:col-span-2 space-y-8">
            {welcome && (
              <Card className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 border-turquoise-500">
                <CardHeader>
                  <CardTitle className="text-2xl font-viking">{welcome.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-sm max-w-none space-y-4">
                    {welcome.content.split('\n\n').map((paragraph, idx) => (
                      <p key={idx} className="leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {(scheduleFriday || scheduleSaturday || scheduleSunday) && (
              <Card className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 border-turquoise-500">
                <CardHeader>
                  <CardTitle className="text-2xl font-viking">Event Schedule</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {scheduleFriday && (
                    <div>
                      <h3 className="font-viking text-lg mb-2">{scheduleFriday.title}</h3>
                      <ul className="space-y-2 text-sm">
                        {scheduleFriday.content.split('\n').map((line, idx) => (
                          <li key={idx}>{line}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {scheduleSaturday && (
                    <div>
                      <h3 className="font-viking text-lg mb-2">{scheduleSaturday.title}</h3>
                      <ul className="space-y-2 text-sm">
                        {scheduleSaturday.content.split('\n').map((line, idx) => (
                          <li key={idx}>{line}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {scheduleSunday && (
                    <div>
                      <h3 className="font-viking text-lg mb-2">{scheduleSunday.title}</h3>
                      <ul className="space-y-2 text-sm">
                        {scheduleSunday.content.split('\n').map((line, idx) => (
                          <li key={idx}>{line}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Card className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 border-turquoise-500">
              <CardHeader>
                <CardTitle className="text-2xl font-viking">Guest Instructors</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm">
                  Learn from world-class instructors! Join us for special workshops and training sessions led by renowned masters in the HEMA community.
                </p>
                <br/>
                <Link to="/instructors">
                  <Button variant="secondary" className="w-full">
                    View Guest Instructors
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {registrationInfo && (
              <Card className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 border-turquoise-500">
                <CardHeader>
                  <CardTitle className="font-viking">{registrationInfo.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {registrationInfo.content.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="text-sm">
                      {paragraph}
                    </p>
                  ))}
                  {!REGISTRATIONS_CLOSED ? (
                    <Link to="/event-registration">
                      <Button variant="secondary" className="w-full">
                        Register Now
                      </Button>
                    </Link>
                  ) : (
                    <ClosedNotice />
                  )}
                </CardContent>
              </Card>
            )}

            {venueInfo && (
              <Card className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 border-turquoise-500">
                <CardHeader>
                  <CardTitle className="font-viking">{venueInfo.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {venueInfo.content.split('\n\n').map((section, idx) => (
                    <div key={idx}>
                      {section.split('\n').map((line, lineIdx) => (
                        <p key={lineIdx} className={lineIdx === 0 ? 'text-sm font-medium' : 'text-xs text-muted-foreground'}>
                          {line}
                        </p>
                      ))}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {hotelInfo && (
              <Card className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 border-turquoise-500">
                <CardHeader>
                  <CardTitle className="font-viking">{hotelInfo.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {hotelInfo.content.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className={idx === 0 ? 'text-sm' : 'text-xs text-muted-foreground'}>
                      {paragraph}
                    </p>
                  ))}
                  <div className="space-y-2">
                    {hotelBookingUrl && (
                      <Button
                        variant="secondary"
                        className="w-full"
                        onClick={() => window.open(hotelBookingUrl, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Book Hotel
                      </Button>
                    )}
                    <Link to="/hotel">
                      <Button variant="default" className="w-full">
                        View Hotel Details
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 border-turquoise-500">
              <CardHeader>
                <CardTitle className="font-viking">Sponsors & Vendors</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm">
                  Thank you to our amazing sponsors and vendors who make Cactus Cup possible!
                </p>
                <br/>
                <Link to="/sponsors">
                  <Button variant="default" className="w-full">
                    View Our Sponsors
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {contact && (
              <Card className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 border-2 border-turquoise-500">
                <CardHeader>
                  <CardTitle className="font-viking">{contact.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {contact.content.split('\n\n').map((paragraph, idx) => {
                    if (paragraph.includes('Email:')) {
                      const email = paragraph.replace('Email: ', '');
                      return (
                        <div key={idx}>
                          <p className="text-sm mb-2">Questions? Reach out to our team:</p>
                          <p className="text-sm">
                            <a
                              href={`mailto:${email}`}
                              className="text-orange-600 hover:text-orange-700 transition-colors font-medium"
                            >
                              {email}
                            </a>
                          </p>
                        </div>
                      );
                    }
                    return (
                      <p key={idx} className="text-sm">
                        {paragraph}
                      </p>
                    );
                  })}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
